/**
 * A keyed store of loaded values that never starts the same load twice.
 *
 * Two maps rather than one: what is loaded, and what is still in flight. Asking for a key that is
 * already loading hands back the same promise instead of firing a second request, which is the whole
 * point of a cache in a game that shows the same sprite in four places.
 *
 * A failed load is not remembered, so a retry after a dropped connection actually retries.
 *
 * Generic over the value, because textures and sounds differ in everything except this bookkeeping.
 */
export class AssetCache<TAsset> {
  readonly #loaded = new Map<string, TAsset>();
  readonly #pending = new Map<string, Promise<TAsset>>();

  /** Number of assets currently loaded. */
  get size(): number {
    return this.#loaded.size;
  }

  /** Keys of every loaded asset, as a fresh array. */
  get keys(): readonly string[] {
    return [...this.#loaded.keys()];
  }

  /** Every loaded asset, as a fresh array. */
  get values(): readonly TAsset[] {
    return [...this.#loaded.values()];
  }

  /** The asset under this key, or undefined when it is missing or still loading. */
  get(key: string): TAsset | undefined {
    return this.#loaded.get(key);
  }

  /** Whether an asset is loaded under this key. */
  has(key: string): boolean {
    return this.#loaded.has(key);
  }

  /** Stores an asset produced elsewhere, replacing whatever was under the key. */
  set(key: string, asset: TAsset): TAsset {
    this.#loaded.set(key, asset);
    return asset;
  }

  /**
   * Returns the asset under `key`, loading it with `create` only if nothing has it yet.
   *
   * Concurrent callers share one `create` call. The result is cached before the promise resolves for
   * anyone, so the second caller can never observe a half-populated cache.
   */
  async load(key: string, create: () => Promise<TAsset>): Promise<TAsset> {
    const loaded = this.#loaded.get(key);
    if (loaded !== undefined) {
      return loaded;
    }

    const inFlight = this.#pending.get(key);
    if (inFlight !== undefined) {
      return inFlight;
    }

    const request = create()
      .then((asset) => this.set(key, asset))
      .finally(() => {
        this.#pending.delete(key);
      });

    this.#pending.set(key, request);
    return request;
  }

  /** Removes an asset and returns it, so the caller can release whatever is behind it. */
  delete(key: string): TAsset | undefined {
    const asset = this.#loaded.get(key);
    this.#loaded.delete(key);
    return asset;
  }

  /** Empties the cache. Releasing the assets themselves is the caller's business. */
  clear(): void {
    this.#loaded.clear();
    this.#pending.clear();
  }
}
